import { SimpleUserAvatar } from '@/components/UserAvatar'
import SimpleUsername from '@/components/SimpleUsername'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import type { Editor } from '@tiptap/core'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { cn } from '@/lib/utils'
import { resolveArgsContext } from './argsFlow'

export type TArgsListItem =
  | { kind: 'choice'; value: string }
  | { kind: 'pubkey'; value: string }
  | { kind: 'relay'; value: string }

export interface ArgsListProps {
  editor: Editor
  items: TArgsListItem[]
  command: (item: TArgsListItem) => void
}

export interface ArgsListHandle {
  onKeyDown: (args: SuggestionKeyDownProps) => boolean
}

function ItemContent({ item }: { item: TArgsListItem }) {
  switch (item.kind) {
    case 'choice':
      return <span className="font-mono text-sm">{item.value}</span>
    case 'pubkey':
      return (
        <div className="flex gap-2 items-center w-72 min-w-0">
          <SimpleUserAvatar user={item.value} />
          <div className="flex-1 w-0 min-w-0">
            <SimpleUsername user={item.value} className="font-semibold truncate" />
            <div className="text-xs text-muted-foreground font-mono truncate">{item.value}</div>
          </div>
        </div>
      )
    case 'relay':
      return <span className="text-sm truncate">{item.value}</span>
  }
}

const ArgsList = forwardRef<ArgsListHandle, ArgsListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const arg = resolveArgsContext(props.editor.state.selection.$from)?.arg

  const selectItem = (index: number) => {
    const item = props.items[index]
    if (item) props.command(item)
  }

  const upHandler = () =>
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
  const downHandler = () => setSelectedIndex((selectedIndex + 1) % props.items.length)
  const enterHandler = () => selectItem(selectedIndex)

  useEffect(() => {
    setSelectedIndex(props.items.length ? 0 : -1)
  }, [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      // nothing to pick: let keys (e.g. Enter for a new line) reach the editor
      if (!props.items.length) return false
      if (event.key === 'ArrowUp') {
        upHandler()
        return true
      }
      if (event.key === 'ArrowDown') {
        downHandler()
        return true
      }
      if (event.key === 'Enter' && selectedIndex >= 0) {
        enterHandler()
        return true
      }
      return false
    }
  }))

  if (!arg) return null

  return (
    <div className="border rounded-lg bg-background z-50 pointer-events-auto flex flex-col max-h-80 overflow-y-auto shadow-md">
      <div className="flex items-baseline gap-2 px-3 py-2 text-xs text-muted-foreground min-w-0 max-w-80">
        <span className="font-mono font-medium text-foreground shrink-0">{arg.name}</span>
        <span className="font-mono shrink-0">
          {arg.type}
          {arg.required ? '' : ' (optional)'}
        </span>
        {arg.label && <span className="truncate">{arg.label}</span>}
      </div>
      {props.items.map((item, index) => (
        <button
          className={cn(
            'cursor-pointer text-start items-center m-1 p-2 outline-none transition-colors rounded-md',
            selectedIndex === index && 'bg-accent text-accent-foreground'
          )}
          key={`${item.kind}:${item.value}:${index}`}
          onClick={() => selectItem(index)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="flex gap-2 items-center w-80 max-w-full truncate pointer-events-none">
            <ItemContent item={item} />
          </div>
        </button>
      ))}
    </div>
  )
})
ArgsList.displayName = 'ArgsList'
export default ArgsList
